import type { ElementNode, ParseResult } from "@openuidev/lang-core";
import { coreWidgets } from "./catalogue/core";
import { contentWidgets } from "./widgets";

// Widgets that are not manipulatives: the layout root, plain text and a question about
// the current item. Every subject widget is something the child works with.
const NOT_MANIPULATIVE: ReadonlySet<string> = new Set([
  "Stack",
  ...coreWidgets.map((widget) => widget.name),
]);
const WIDGET_NAMES: ReadonlySet<string> = new Set(contentWidgets.map((widget) => widget.name));

/**
 * Whether a validated program holds a manipulative (a subject widget such as a number
 * line or fraction bars). The engine puts those beside the exercise, on the workbench
 * (ADR-0044); text and questions stay in the conversation.
 */
export function isManipulative(result: ParseResult): boolean {
  return widgetNames(result).some((name) => WIDGET_NAMES.has(name) && !NOT_MANIPULATIVE.has(name));
}

/** The widget name of every element of a validated program, depth first, in source order. */
export function widgetNames(result: ParseResult): readonly string[] {
  const names: string[] = [];
  if (result.root !== null) visit(result.root, (node) => names.push(node.typeName));
  return names;
}

/**
 * Item ids referenced by `ChoiceQuestion` widgets in a validated program, in order.
 * Callers check them against the engine's current item (CLAUDE.md rule 1: the model
 * never picks an item); a program that references any other item is dropped.
 */
export function referencedItemIds(result: ParseResult): readonly string[] {
  const ids: string[] = [];
  if (result.root === null) return ids;
  visit(result.root, (node) => {
    if (node.typeName !== "ChoiceQuestion") return;
    const itemId = node.props["itemId"];
    // A validated ChoiceQuestion always has a string id; anything else counts as foreign.
    ids.push(typeof itemId === "string" ? itemId : "");
  });
  return ids;
}

/**
 * The text of a program that is one `BoardStep` and nothing else (ADR-0052, step B2), or
 * null. Only such a program can go on the board; anything else is shown as usual.
 */
export function boardStepText(result: ParseResult): string | null {
  const names = widgetNames(result).filter((name) => name !== "Stack");
  if (names.length !== 1 || names[0] !== "BoardStep" || result.root === null) return null;
  let text: string | null = null;
  visit(result.root, (node) => {
    const value = node.props["text"];
    if (node.typeName === "BoardStep" && typeof value === "string") text = value.trim();
  });
  return text;
}

/** Every element of a tree, depth first, in source order. */
function visit(node: ElementNode, each: (node: ElementNode) => void): void {
  each(node);
  for (const value of Object.values(node.props)) {
    for (const child of Array.isArray(value) ? (value as unknown[]) : [value]) {
      if (isElement(child)) visit(child, each);
    }
  }
}

function isElement(value: unknown): value is ElementNode {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { type?: unknown }).type === "element" &&
    typeof (value as { typeName?: unknown }).typeName === "string"
  );
}
